import { AfterContentInit, ChangeDetectorRef, Component, forwardRef, Injector, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { ButtonComponent } from '../../button/button.component';
@Component({
  selector: 'lib-form-file-attachment',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './form-file-attachment.component.html',
  styleUrl: './form-file-attachment.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormFileAttachmentComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormFileAttachmentComponent),
      multi: true,
    },
  ],
})
export class FormFileAttachmentComponent extends BaseControlValueAccessor<File[]> implements AfterContentInit, ControlValueAccessor, Validator {
  label = input<string | null>(null);
  acceptedFormats = input<string[]>(['PDF', 'XLSX', 'DOCX', 'JPG', 'PNG']);
  placeholder = input<string | null>('Drag and drop files or click to browse');
  multiple = input<boolean>(true);
  control!: FormControl;
  files: File[] = [];
  errorMessage: string | null = null;
  maxWidth = input<string | null>('');
  controlName: string | null = 'browseFiles';

  constructor(private injector: Injector, private cdRef: ChangeDetectorRef) {
    super();
  }

  ngAfterContentInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null) as NgControl;
    if (ngControl) {
      this.control = ngControl.control as FormControl;
      this.controlName = ngControl.name as string;
    } else {
      this.control = new FormControl();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  public get invalid(): boolean | null {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean | null {
    if (!this.control) {
      return false;
    }
    const { dirty, touched } = this.control;
    return this.invalid ? dirty || touched : false;
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.addFiles(Array.from(target.files));
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  onDragOver(event: Event): void {
    event.preventDefault();
  }

  addFiles(newFiles: File[]): void {
    const currentFiles = this.files;
    const updatedFiles = this.multiple() ? [...currentFiles] : [];
    let invalidFiles = false;

    const acceptedFormatsUppercase = this.acceptedFormats().map(format => format.toUpperCase());

    newFiles.forEach((file) => {
      const fileExtension = file.name.split('.').pop()?.toUpperCase();
      if (!acceptedFormatsUppercase.includes(fileExtension!)) {
        invalidFiles = true;
        return;
      }

      if (!currentFiles.some((existingFile) => existingFile.name === file.name)) {
        updatedFiles.push(file);
      }
    });

    if (invalidFiles) {
      this.errorMessage = `Some files were not added. Only ${this.acceptedFormats().join(', ').toUpperCase()} files are allowed.`;
    } else {
      this.errorMessage = null;
    }

    this.files = updatedFiles;
    this.value = this.files;
    this.onChange(updatedFiles);
  }

  removeFile(index: number): void {
    const updatedFiles = this.files.filter((_, i) => i !== index);
    this.files = updatedFiles;
    this.value = this.files;
    this.onChange(updatedFiles);
  }

  override writeValue(value: File[]): void {
    this.files = value || [];
    this.value = this.files;
    this.cdRef.markForCheck();
  }

  override registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  override registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public override onChange = (_: File[]) => {};
  public override onTouched = () => {};
}
